import { nextTick } from 'vue'
import type { RouteLocation } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useStageStore } from '@/stores/stage'
import { Actions, type Subjects } from '@/rules/abilityTypes'
import { canRoute, type AbsolutePath } from '@/lib/canRoute'
import userRoutes from './userRoutes'
import projectRoutes from './assignRoutes'
import masterRoutes from './masterRoutes'
import jobRoutes from './jobRoutes'
import settingsRoutes from './settingsRoutes'
import { createStack } from 'vue-stacked-ui'
import i18next from 'i18next'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    stackable?: boolean
    action?: Actions
    subject?: Subjects
    titleKey?: string
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: {
        titleKey: 'ui.router.login'
      }
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('../views/LogoutView.vue'),
      meta: {
        titleKey: 'ui.router.logout'
      }
    },
    {
      path: '/foreget_password',
      name: 'foreget_password',
      component: () => import('../views/ForgetPassword.vue')
    },
    {
      path: '/reset_password/:oobCode',
      name: 'reset_password',
      props: true,
      component: () => import('../views/ResetPassword.vue')
    },
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
      meta: {
        requiresAuth: true,
        requiresDB: true
      }
    },
    {
      path: '/_diagnosis',
      name: 'diagnosis',
      component: () => import('../views/SystemDiagnosis.vue'),
      meta: {
        requiresAuth: true,
        requiresDB: true
      }
    },
    {
      path: '/_sqlite',
      name: 'sqlite',
      component: () => import('../views/QuerySQLite.vue'),
      meta: {
        requiresAuth: true,
        requiresDB: true
      }
    },
    {
      path: '/_styleguide',
      name: 'styleguide',
      component: () => import('../views/StyleGuide.vue'),
      meta: {
        requiresAuth: true,
        requiresDB: true
      }
    },
    {
      path: '/_job_trigger',
      name: 'job_trigger',
      component: () => import('../views/JobTriggerManager.vue'),
      meta: {
        requiresAuth: true,
        requiresDB: true
      }
    },
    {
      path: '/forbidden',
      name: 'forbidden',
      component: () => import('../views/ForbiddenView.vue'),
      meta: {
        requiresAuth: true,
        titleKey: 'ui.router.forbidden'
      }
    },
    ...userRoutes,
    ...projectRoutes,
    ...masterRoutes,
    ...jobRoutes,
    ...settingsRoutes,
    // 404 Not Found - キャッチオールルート（最後に配置する必要がある）
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      component: () => import('../views/NotFoundView.vue'),
      meta: {
        titleKey: 'ui.router.notFound'
      }
    }
  ]
})

export const stack = createStack(router)

stack.beforeEach(async (to: RouteLocation) => {
  // Check if route requires authentication
  if (to.matched?.some((r) => r.meta.requiresAuth)) {
    const userStore = useUserStore()
    if (!userStore.isLoggedIn) {
      router.push({ name: 'login' })
      // cancel stacked page
      return false
    }
  }

  const action = to.meta.action
  const subject = to.meta.subject
  if (!subject || !action) {
    return
  }
  const hasPermission = await canRoute(to.fullPath as AbsolutePath)
  if (hasPermission) {
    return
  }
  // 権限エラー時もモーダルを閉じる
  const stageStore = useStageStore()
  stageStore.clear()
  return { name: 'forbidden' }
})

const TITLE_PREFIX = 'Co-Assign | '
const DEFAULT_TITLE_KEY = 'ui.router.appTitle'

router.afterEach((to) => {
  nextTick(() => {
    const titleKey = to.meta.titleKey ?? DEFAULT_TITLE_KEY
    const title = i18next.t(titleKey)
    document.title = TITLE_PREFIX + title
  })
})

export default router
